import React, { useEffect, useState } from 'react';

import DateFnsUtils from '@date-io/date-fns';
import { Link } from 'react-router-dom';
import { Popover, LinearProgress } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import { parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import AdjustIcon from 'images/icons/config/adjustIcon.svg';

import {
  Card,
  CardHeader,
  Icon,
  TableWrapper,
  Table,
  Th,
  Td,
  StatusBadge,
  HeaderInfo,
  HeaderIconWrapper,
  HeaderTextWrapper,
  CardTitle,
  CardMeta,
  MetaBadge,
  IconDateContainer,
  DataInputWrapper,
  DataInputWithIcon,
  DataInputIcon
} from '../style';

const normalize = (str) =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const getActionRoute = (cardType, actionName, restaurantId) => {
  const key = normalize(actionName);

  if (cardType === 'operacao' && key.includes('producao')) {
    return `/producao/${restaurantId}`;
  }

  const routes = {
    operacao: {
      recebimento: `/recebimento/${restaurantId}`,
      producao: `/producao/${restaurantId}`,
      contagem: `/inventario/contagens/${restaurantId}`,
      copiloto: `/copiloto/${restaurantId}`
    },
    analise: {
      producao: `/programacao-diaria/${restaurantId}`,
      contagem: `/prog-diaria-contagem/${restaurantId}`
    },
    evolucao: {
      'plano de acao producoes': `/programacao-diaria/${restaurantId}`,
      'plano de acao contagens': `/prog-diaria-contagem/${restaurantId}`,
      'projecao vendas': `/admin/projecao-de-vendas/cadastro/${restaurantId}`
    }
  };

  return routes[cardType]?.[key] || null;
};

function HomeCards({
  isLoading,
  isFetching,
  type,
  title,
  icon,
  meta,
  tasks,
  onSettingsClick,
  selectedDate,
  onDateChange,
  isConfigured,
  color,
  restaurantId
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [expanded, setExpanded] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  const hasConfiguredTasks = tasks?.some(
    (task) => task.previsto && task.previsto.trim() !== '-'
  );

  const cardVariant = isLoading
    ? undefined
    : hasConfiguredTasks
    ? 'ativo'
    : 'inativo';

  useEffect(() => {
    setExpanded(!isMobile);
  }, [isMobile]);

  if (isLoading) {
    return <Card type={type}>Carregando...</Card>;
  }

  const handleOpenCalendar = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseCalendar = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Card type={type} variant={cardVariant} color={color}>
      <CardHeader
        type={type}
        variant={cardVariant}
        onClick={() => {
          if (isMobile) setExpanded((prev) => !prev);
        }}
        isConfigured={isConfigured}
        color={color}
        style={{ cursor: isMobile ? 'pointer' : 'default' }}
      >
        <HeaderInfo>
          <HeaderIconWrapper>{icon}</HeaderIconWrapper>
          <HeaderTextWrapper>
            <CardTitle type={type} variant={cardVariant}>
              {title}
            </CardTitle>
            <CardMeta type={type} variant={cardVariant}>
              <span className="metaLabel">atingimento das metas:</span>
              <MetaBadge type={type} variant={cardVariant} color={color}>
                {meta.total
                  ? `${meta.atingidas}/${meta.total}`
                  : meta.atingidas}
              </MetaBadge>
            </CardMeta>
          </HeaderTextWrapper>
        </HeaderInfo>

        <IconDateContainer>
          <Icon
            className="mt8"
            type={type}
            disabled={isMobile && !hasConfiguredTasks}
            onClick={(e) => {
              e.stopPropagation();
              onSettingsClick();
            }}
          >
            <img src={AdjustIcon} alt="Ajustar valores" />
          </Icon>

          {!isMobile ? (
            <DataInputWrapper>
              <DataInputIcon
                onClick={handleOpenCalendar}
                variant={cardVariant}
              />
              <DataInputWithIcon
                as="input"
                type="text"
                value={
                  selectedDate
                    ? selectedDate.split('/').slice(0, 2).join('/')
                    : ''
                }
                readOnly
                onClick={handleOpenCalendar}
                variant={cardVariant}
              />
            </DataInputWrapper>
          ) : null}

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleCloseCalendar}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
          >
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
              <DatePicker
                variant="static"
                value={
                  selectedDate
                    ? parse(selectedDate, 'dd/MM/yyyy', new Date())
                    : null
                }
                onChange={(date) => {
                  if (date) {
                    const formatted = new Intl.DateTimeFormat('pt-BR').format(
                      date
                    );
                    onDateChange(formatted);
                  } else {
                    onDateChange('');
                  }
                  handleCloseCalendar();
                }}
              />
            </MuiPickersUtilsProvider>
          </Popover>
        </IconDateContainer>

        {expanded && isMobile ? (
          <DataInputWrapper>
            <DataInputIcon onClick={handleOpenCalendar} variant={cardVariant} />
            <DataInputWithIcon
              as="input"
              type="text"
              value={
                selectedDate
                  ? selectedDate.split('/').slice(0, 2).join('/')
                  : ''
              }
              readOnly
              onClick={handleOpenCalendar}
              variant={cardVariant}
            />
          </DataInputWrapper>
        ) : null}
      </CardHeader>

      {isFetching && (
        <div>
          <LinearProgress />
        </div>
      )}

      {expanded && (
        <TableWrapper>
          <Table type={type}>
            <thead>
              <tr>
                <Th type={type} variant={cardVariant}>
                  AÇÃO
                </Th>
                <Th type={type} variant={cardVariant}>
                  PREVISTO
                </Th>
                <Th type={type} variant={cardVariant}>
                  REALIZADO
                </Th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task, index) => {
                const baseRoute = getActionRoute(
                  type,
                  task.acao?.toLowerCase(),
                  restaurantId
                );
                const formatDateToURL = (brDate) => {
                  if (!brDate) return '';
                  const [day, month, year] = brDate.split('/');
                  return `${year}-${month}-${day}`;
                };

                const effectiveDate = task.routeDate || selectedDate;
                const formattedDate = formatDateToURL(effectiveDate);
                 
                const route = baseRoute
                  ? `${baseRoute}?startAt=${formattedDate}&endAt=${formattedDate}`
                  : null;

                const rowContent = (
                  <>
                    <Td type={type} variant={cardVariant}>
                      {task.acao}
                    </Td>
                    <Td variant={cardVariant}>{task.previsto}</Td>
                    <Td variant={cardVariant}>
                      <StatusBadge status={task.status}>
                        {task.realizado}
                      </StatusBadge>
                    </Td>
                  </>
                );

                return (
                  <tr
                    key={index}
                    style={{ cursor: route ? 'pointer' : 'default' }}
                  >
                    {route ? (
                      <Link
                        to={route}
                        target="_blank"
                        style={{
                          display: 'contents',
                          textDecoration: 'none',
                          color: 'inherit'
                        }}
                      >
                        <Td type={type} variant={cardVariant}>
                          {task.acao}
                        </Td>
                        <Td variant={cardVariant}>{task.previsto}</Td>
                        <Td variant={cardVariant}>
                          <StatusBadge status={task.status}>
                            {task.realizado}
                          </StatusBadge>
                        </Td>
                      </Link>
                    ) : (
                      <>
                        <Td type={type} variant={cardVariant}>
                          {task.acao}
                        </Td>
                        <Td variant={cardVariant}>{task.previsto}</Td>
                        <Td variant={cardVariant}>
                          <StatusBadge status={task.status}>
                            {task.realizado}
                          </StatusBadge>
                        </Td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      )}
    </Card>
  );
}

export default HomeCards;
